import React from 'react';
import './Callout.scss';
import { Eyebrow } from '../Eyebrow/Eyebrow';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { SmartLink } from '../SmartLink/SmartLink';
import { KiteGrid, KiteGridCell } from '@kite/react-kite';
import { AnalyticsController } from '../../layout/Analytics';
import { CURRENT_SITE } from '../../../config-sites';
import { useInlineImages } from '../../shared/useInlineImages';

export interface CalloutInputs {
    calloutImage: {
        gatsbyImage: any;
        alt: string;
        verticalAlign?: `top` | `middle` | `bottom`;
    };
    bgColor: string;
    imagePosition: string;
    title: string;
    eyebrowText?: string;
    swapEyebrowOrder?: boolean;
    isMainContent?: boolean;
    textContent: string;
    cta: {
        uri: string;
        title: string;
        download?: boolean;
    }[];
    policyOverviewBlock?: boolean;
}

export const Callout = ({ inputs }: { inputs: CalloutInputs }) => {
    const gImage = getImage(inputs.calloutImage?.gatsbyImage);
    const imageVertical = inputs.calloutImage?.verticalAlign;
    const wrapperClassNames = [`paragraph__callout`];
    wrapperClassNames.push(inputs.imagePosition);
    wrapperClassNames.push(inputs.bgColor);
    if (inputs.policyOverviewBlock) {
        wrapperClassNames.push(`policy-overview`);
    }

    if (inputs.textContent) {
        inputs.textContent = inputs.textContent.replaceAll(
            `/sites/default/files`,
            `${CURRENT_SITE.endpoint}/sites/default/files`
        );
    }

    return (
        <article className={wrapperClassNames.join(` `)}>
            <section className={`${inputs.imagePosition} kite-container`}>
                <KiteGrid>
                    <KiteGridCell col={8} className="content">
                        {!inputs.isMainContent && (
                            <>
                                {inputs.eyebrowText && (
                                    <Eyebrow
                                        text={inputs.eyebrowText}
                                        bg={
                                            inputs.bgColor === `dark-blue` ||
                                            inputs.bgColor === `dark_blue`
                                                ? `dark`
                                                : `light`
                                        }
                                    />
                                )}
                                <h2
                                    className={`typestack-title-3 ${
                                        inputs.swapEyebrowOrder ? `swap` : ``
                                    }`}
                                >
                                    {inputs.title}
                                </h2>
                            </>
                        )}
                        {inputs.isMainContent && (
                            <>
                                <h1 className="typestack-title-3">
                                    {inputs.title}
                                </h1>
                                {inputs.eyebrowText && (
                                    <h2>
                                        <Eyebrow
                                            text={inputs.eyebrowText}
                                            bg={
                                                inputs.bgColor === `dark-blue`
                                                    ? `dark`
                                                    : `light`
                                            }
                                        />
                                    </h2>
                                )}
                            </>
                        )}
                        <div
                            className="text"
                            dangerouslySetInnerHTML={{
                                __html: useInlineImages(inputs.textContent),
                            }}
                        ></div>
                        {inputs.cta.length > 0 &&
                            inputs.cta[0].title &&
                            inputs.cta.map((link, idx) => (
                                <SmartLink
                                    key={idx}
                                    button={true}
                                    url={link.uri}
                                    ariaLabel={`${link.title} - ${inputs.title}`}
                                    download={link.download}
                                    onClick={() => {
                                        AnalyticsController.track({
                                            event: `component click`,
                                            componentLink: `${inputs.title}: ${link.title}`,
                                        });
                                    }}
                                >
                                    {link.title}
                                </SmartLink>
                            ))}
                    </KiteGridCell>
                    {gImage && (
                        <GatsbyImage
                            className={`callout__image kite-cell kite-cell-4 ${imageVertical}`}
                            alt={inputs.calloutImage.alt}
                            image={gImage}
                        />
                    )}
                    {!gImage && (
                        <div className={`callout__image kite-cell kite-cell-4 ${imageVertical}`}></div>
                    )}
                </KiteGrid>
            </section>
        </article>
    );
};
